import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import ReactDOM from "react-dom";
import { useInView } from "react-intersection-observer";
import { useHistory } from "react-router-dom";

import * as Sentry from "@sentry/react";
import { noop } from "lodash";

import { DismissibleElementsContext } from "~/common/DismissibleElements";

import ConfettiCannons from "./ConfettiCannons";
import Tours, { isOnSightTourKey, type Tour, type TourKey } from "./Tours";
import Tourtip from "./Tourtip";

import styles from "./TourController.module.css";

export const TOUR_NAVIGATION_EVENT = "tour-navigation";

export class TourNavigationEvent extends CustomEvent<{ path: string }> {
	constructor(path: string) {
		super(TOUR_NAVIGATION_EVENT, { detail: { path } });
	}
}

declare global {
	interface WindowEventMap {
		[TOUR_NAVIGATION_EVENT]: TourNavigationEvent;
	}
}

type TourContextValue = {
	startTour: (key: TourKey) => void;
	advanceTour: () => void;
	activeTour: Tour | null;
	activeTourKey: TourKey | null;
	abortTour: () => void;
	stepIndex: number;
	dropConfetti: () => void;
	isTestingMode: boolean;
	toggleTestingMode: () => void;
};

const TourContext = createContext<TourContextValue>({
	startTour: noop,
	advanceTour: noop,
	activeTour: null,
	activeTourKey: null,
	abortTour: () => {
		throw new Error("Called outside of TourContext");
	},
	stepIndex: 0,
	dropConfetti: noop,
	isTestingMode: false,
	toggleTestingMode: noop,
});
TourContext.displayName = "TourContext";
export const useTourContext = () => useContext(TourContext);

const horizontalShaking = [
	{ transform: "translateX(0)" },
	{ transform: "translateX(5px)" },
	{ transform: "translateX(-5px)" },
	{ transform: "translateX(5px)" },
	{ transform: "translateX(-5px)" },
	{ transform: "translateX(5px)" },
	{ transform: "translateX(-5px)" },
	{ transform: "translateX(5px)" },
	{ transform: "translateX(0)" },
];

const shakeAnimationTiming = {
	duration: 500,
	iterations: 1,
};

const shakeTourtip = () => {
	const tourtip = document.querySelector(`[data-tour-tooltip]`);
	tourtip?.animate(horizontalShaking, shakeAnimationTiming);
};

/**
 * A function that ensures a minimum duration has passed before resolving.
 * @param duration - The minimum duration in milliseconds
 * @returns A promise that resolves when the minimum duration has passed
 */
function createSpeedBump(duration: number) {
	const startTime = performance.now();

	return new Promise<void>(resolve => {
		const speedBump = performance.now() - startTime;
		if (speedBump < duration) setTimeout(resolve, duration - speedBump);
		else resolve();
	});
}

export default function TourController({ children }: React.PropsWithChildren<{}>) {
	const [isTestingMode, setIsTestingMode] = useState(false);
	const toggleTestingMode = useCallback(() => setIsTestingMode(prev => !prev), []);

	const [activeTourKey, setActiveTourKey] = React.useState<TourKey | null>(null);
	const activeTour = React.useMemo(() => (activeTourKey ? Tours[activeTourKey] : null), [activeTourKey]);
	const [stepIndex, setStepIndex] = React.useState(0);

	// The "preparing" state corresponds to whether the `prepareApp` function in a step has yet to be resolved.
	// It is not a general loading state, and it being false doesn't mean the tooltip is visible.
	// It is used to communicate when we should expect the tooltip target to be visible without further control.
	const [isPreparing, setIsPreparing] = useState(false);

	// Refs the element that we wish to attach the tourtip to.
	// The "regular" ref is needed for accessing the HTMLElement through ref.current.
	// The ref returned by useInView needs to be attached to that element,
	// 	but it doesn't offer access to the actual HTMLElement through ref.current.
	// See here: https://github.com/thebuilder/react-intersection-observer?tab=readme-ov-file#how-can-i-assign-multiple-refs-to-a-component
	const tourtipTargetRegularRef = useRef<HTMLElement | null>(null);
	const { ref: tourtipTargetInViewRef, inView } = useInView({
		threshold: 1,
	});

	// This method effectively combines the two refs above, so that they stay in sync.
	const setTourtipTargetRefs = useCallback(
		node => {
			tourtipTargetRegularRef.current = node;
			tourtipTargetInViewRef(node);
		},
		[tourtipTargetInViewRef],
	);

	// A ref to the element that enables the "spotlight" effect
	const spotlightRef = useRef<HTMLDivElement | null>(null);

	// Position information for the element referred to be spotlightRef. It it always positioned to have the same
	// dimensions as the tooltip target element, whether or not a spotlight is visible on screen.
	const [spotlightRect, setSpotlightRect] = useState<Pick<
		DOMRect,
		"top" | "left" | "right" | "bottom" | "width" | "height"
	> | null>(null);

	// A state and effect that disables interactivity with everywhere that is NOT covered by the spotlight.
	const [isMouseOnSpotlight, setIsMouseOnSpotlight] = useState(false);
	useEffect(() => {
		if (!activeTour?.steps[stepIndex]?.showSpotlight || isPreparing || !spotlightRect) {
			setIsMouseOnSpotlight(false);
			return;
		}

		const handleMouseMove = (e: MouseEvent) => {
			if (activeTour?.steps[stepIndex]?.allowInteraction) {
				const isMouseOverSpotlight =
					e.clientX >= spotlightRect.left &&
					e.clientX <= spotlightRect.right &&
					e.clientY >= spotlightRect.top &&
					e.clientY <= spotlightRect.bottom;
				setIsMouseOnSpotlight(isMouseOverSpotlight);
			}
		};

		document.addEventListener("mousemove", handleMouseMove);

		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
		};
	}, [stepIndex, activeTour?.steps, isPreparing, spotlightRect]);

	// This effect is responsible for finding and tracking the position of the target element for the tourtip.
	useLayoutEffect(() => {
		let animationFrame: number | null = null;

		const findTarget = () => {
			if (!activeTour) return;

			// First try finding the target element
			const activeStep = activeTour.steps[stepIndex];
			const target = document.querySelector<HTMLElement>(activeStep.tooltipTarget);

			if (!target || isPreparing) {
				// Clear the spotlight position if the target cannot be found, or if the step is still being prepared.
				setSpotlightRect(null);
			} else {
				const newTargetRect = target.getBoundingClientRect();

				setSpotlightRect(prev => {
					// If the target's position has changed, update the spotlight position
					if (
						!prev ||
						// If the target's position has changed
						newTargetRect.top !== prev.top ||
						newTargetRect.left !== prev.left ||
						newTargetRect.width !== prev.width ||
						newTargetRect.height !== prev.height
					) {
						return {
							top: newTargetRect.top - (activeStep.tooltipTargetPadding?.top ?? 0),
							left: newTargetRect.left - (activeStep.tooltipTargetPadding?.left ?? 0),
							right: newTargetRect.right + (activeStep.tooltipTargetPadding?.right ?? 0),
							bottom: newTargetRect.bottom + (activeStep.tooltipTargetPadding?.bottom ?? 0),
							width:
								newTargetRect.width +
								(activeStep.tooltipTargetPadding?.left ?? 0) +
								(activeStep.tooltipTargetPadding?.right ?? 0),
							height:
								newTargetRect.height +
								(activeStep.tooltipTargetPadding?.top ?? 0) +
								(activeStep.tooltipTargetPadding?.bottom ?? 0),
						};
					} else {
						return prev;
					}
				});

				// Sometimes a new element may satisfy the tooltip target selector. If this happens, update its refs.
				if (target !== tourtipTargetRegularRef.current) {
					setTourtipTargetRefs(target);
				}
			}

			// requestAnimationFrame is a "one-off" function, and relies on recursion to be called on every frame.
			animationFrame = window.requestAnimationFrame(findTarget);
		};

		// By using requestAnimationFrame, we ensure that the spotlight position is updated even if the target element moves.
		animationFrame = window.requestAnimationFrame(findTarget);

		return () => {
			if (animationFrame) window.cancelAnimationFrame(animationFrame);
		};
	}, [activeTour, stepIndex, setTourtipTargetRefs, isPreparing]);

	let history = useHistory();

	useEffect(() => {
		function onTourNavigation(event: TourNavigationEvent) {
			history.push(event.detail.path);
		}

		window.addEventListener(TOUR_NAVIGATION_EVENT, onTourNavigation);

		return () => {
			window.removeEventListener(TOUR_NAVIGATION_EVENT, onTourNavigation);
		};
	}, [history]);

	const startTour = useCallback(async (key: TourKey) => {
		const speedBump = createSpeedBump(200);
		setActiveTourKey(key);
		setIsPreparing(true);

		await Tours[key].steps[0].prepareApp?.();

		// If less than 200ms has passed, wait until 200ms has passed
		await speedBump;

		setIsPreparing(false);
		setStepIndex(0);
	}, []);

	const { markAsSeen, dismiss } = useContext(DismissibleElementsContext);

	const finishTour = useCallback(
		(aborted = false) => {
			setActiveTourKey(null);
			setIsPreparing(false);
			setSpotlightRect(null);
			setStepIndex(0);
			setTourtipTargetRefs(null);
			activeTour?.finalStep?.();

			if (aborted || !activeTourKey) return;

			if (isOnSightTourKey(activeTourKey)) {
				markAsSeen(activeTourKey);
			}

			if (activeTourKey === "EARLY_ACCESS_TOUR") {
				dismiss(activeTourKey);
			}
		},
		[setTourtipTargetRefs, activeTourKey, markAsSeen, activeTour, dismiss],
	);

	const advanceTour = useCallback(async () => {
		if (!activeTour) return;

		const speedBump = createSpeedBump(200);
		setIsPreparing(true);
		setSpotlightRect(null);

		if (stepIndex + 1 >= activeTour.steps.length) {
			finishTour();
			return;
		}

		await activeTour.steps[stepIndex + 1].prepareApp?.();

		// If less than 200ms has passed, wait until 200ms has passed
		await speedBump;

		setIsPreparing(false);
		setStepIndex(prev => prev + 1);
	}, [activeTour, stepIndex, finishTour]);

	const abortTour = useCallback(() => {
		finishTour(true);
	}, [finishTour]);

	// An effect that adds an event listener that advances the tour when the user clicks on the interaction target,
	// only if an interaction target is specified for this step.
	useEffect(() => {
		const interactionTarget = activeTour?.steps[stepIndex].interactionTarget;
		if (!interactionTarget || isPreparing) return;

		const advanceOnDesiredInteraction = (event: MouseEvent) => {
			if (!(event.target as Element).closest(interactionTarget)) return;

			// We want to disable clicking right away
			setIsMouseOnSpotlight(false);

			advanceTour();
		};

		document.addEventListener("click", advanceOnDesiredInteraction);

		return () => {
			document.removeEventListener("click", advanceOnDesiredInteraction);
		};
	}, [activeTour, stepIndex, advanceTour, isPreparing]);

	const [isRainingConfetti, setIsRainingConfetti] = useState(false);
	const dropConfetti = useCallback(() => {
		setIsRainingConfetti(true);

		return () => setIsRainingConfetti(false);
	}, []);

	// A side effect for scrolling the spotlight into view if it's off screen
	useEffect(() => {
		if (inView) return;

		const timeout = setTimeout(() => {
			tourtipTargetRegularRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
		}, 1000);

		return () => clearTimeout(timeout);
	}, [inView]);

	// A side effect that cancels the tour if something is wrong
	useEffect(() => {
		if (!activeTourKey || (!isPreparing && spotlightRect)) return;

		const timeout = window.setTimeout(() => {
			// TODO: A toast would be great, but ~/ui/toast doesn't seem to work here... why?

			Sentry.captureMessage("Tour is stuck, aborting.");
			finishTour(true);
		}, 7000);

		return () => clearTimeout(timeout);
	}, [activeTourKey, finishTour, isPreparing, spotlightRect, startTour]);

	return (
		<TourContext.Provider
			value={useMemo(
				(): TourContextValue => ({
					activeTour,
					startTour,
					advanceTour,
					abortTour,
					activeTourKey,
					stepIndex,
					dropConfetti,
					isTestingMode,
					toggleTestingMode,
				}),
				[
					startTour,
					activeTour,
					stepIndex,
					dropConfetti,
					isTestingMode,
					toggleTestingMode,
					advanceTour,
					abortTour,
					activeTourKey,
				],
			)}
		>
			<ConfettiCannons isRainingConfetti={isRainingConfetti} setIsRainingConfetti={setIsRainingConfetti} />
			{children}
			{activeTour &&
				ReactDOM.createPortal(
					<>
						<div
							id="tour-overlay"
							className={styles.tourOverlay}
							style={{
								pointerEvents: isMouseOnSpotlight ? "none" : "auto",
								cursor: !isPreparing && spotlightRect ? "not-allowed" : "wait",
								backgroundColor:
									activeTour.steps[stepIndex]?.showSpotlight && !isPreparing && spotlightRect
										? "var(--tourOverlayColor)"
										: "transparent",
							}}
							onClick={shakeTourtip}
						>
							{!isPreparing && spotlightRect && (
								<div
									id="tour-spotlight"
									ref={spotlightRef}
									className={styles.tourSpotlight}
									style={{
										top: spotlightRect.top,
										left: spotlightRect.left,
										width: spotlightRect.width,
										height: spotlightRect.height,
									}}
								/>
							)}
						</div>

						{!isPreparing && spotlightRect && (
							<Tourtip
								step={activeTour.steps[stepIndex]}
								index={stepIndex}
								size={activeTour.steps.length}
								targetRef={spotlightRef}
								advanceTour={advanceTour}
							/>
						)}
					</>,
					document.body,
				)}
		</TourContext.Provider>
	);
}
